import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../store';
import {
  activateUsuarioThunk,
  clearFilters,
  clearMutationError,
  createUsuarioThunk,
  deactivateUsuarioThunk,
  fetchPermissionsThunk,
  fetchUsuarioDetailThunk,
  fetchUsuariosThunk,
  selectUser,
  setFilters,
  setLimit,
  setPage,
  updatePermissionsThunk,
  updateUsuarioThunk,
} from '../store/slices/usuarios.slice';
import {
  CreateUsuarioPayload,
  UpdatePermissionsPayload,
  UpdateUsuarioPayload,
  UsuariosFilters,
} from '../types/usuario';

export const useUsuarios = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.usuarios);

  const refresh = useCallback(() => {
    return dispatch(
      fetchUsuariosThunk({
        page: state.page,
        limit: state.limit,
        ...state.filters,
      }),
    );
  }, [dispatch, state.page, state.limit, state.filters]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (state.permissionsStatus === 'idle' && state.permissions.length === 0) {
      dispatch(fetchPermissionsThunk());
    }
  }, [dispatch, state.permissionsStatus, state.permissions.length]);

  return {
    items: state.items,
    page: state.page,
    limit: state.limit,
    total: state.total,
    totalPages: state.totalPages,
    filters: state.filters,
    status: state.status,
    error: state.error,
    setFilters: (filters: UsuariosFilters) => dispatch(setFilters(filters)),
    clearFilters: () => dispatch(clearFilters()),
    setPage: (page: number) => dispatch(setPage(page)),
    setLimit: (limit: number) => dispatch(setLimit(limit)),
    refresh,
    selectedUserId: state.selectedUserId,
    selectedDetail: state.selectedDetail,
    detailStatus: state.detailStatus,
    detailError: state.detailError,
    openDrawer: (id: string) => {
      dispatch(selectUser(id));
      return dispatch(fetchUsuarioDetailThunk(id));
    },
    closeDrawer: () => dispatch(selectUser(null)),
    create: async (payload: CreateUsuarioPayload) => {
      const result = await dispatch(createUsuarioThunk(payload));
      if (createUsuarioThunk.fulfilled.match(result)) {
        await refresh();
      }
      return result;
    },
    update: async (id: string, payload: UpdateUsuarioPayload) => {
      const result = await dispatch(updateUsuarioThunk({ id, payload }));
      if (updateUsuarioThunk.fulfilled.match(result)) {
        await refresh();
      }
      return result;
    },
    activate: async (id: string) => {
      const result = await dispatch(activateUsuarioThunk(id));
      if (activateUsuarioThunk.fulfilled.match(result)) {
        await refresh();
      }
      return result;
    },
    deactivate: async (id: string) => {
      const result = await dispatch(deactivateUsuarioThunk(id));
      if (deactivateUsuarioThunk.fulfilled.match(result)) {
        await refresh();
      }
      return result;
    },
    updatePermissions: async (id: string, payload: UpdatePermissionsPayload) => {
      const result = await dispatch(updatePermissionsThunk({ id, payload }));
      if (updatePermissionsThunk.fulfilled.match(result)) {
        await refresh();
      }
      return result;
    },
    permissions: state.permissions,
    permissionsStatus: state.permissionsStatus,
    loadPermissions: () => dispatch(fetchPermissionsThunk()),
    mutating: state.mutating,
    mutationError: state.mutationError,
    clearMutationError: () => dispatch(clearMutationError()),
  };
};
